import React from "react";
import { motion } from "framer-motion";
import { MessageSquare, Workflow, GraduationCap, Bot, Globe, Share2, ArrowUpRight } from "lucide-react";
import { fadeUp, stagger, viewport } from "./anim";

const services = [
  {
    id: "training",
    testid: "service-card-training",
    icon: GraduationCap,
    span: "md:col-span-7",
    title: "AI Adoption Training",
    body: "The part most consultants skip. I don't just hand you a tool and walk away — I teach your team how to actually use it, with real hands-on training built from years of adult-education experience.",
    tags: ["Hands-on", "Any skill level", "Ph.D.-led"],
  },
  {
    id: "chatbots",
    testid: "service-card-chatbots",
    icon: MessageSquare,
    span: "md:col-span-5",
    title: "AI Chatbots",
    body: "Custom-built assistants that answer questions, qualify leads, and book appointments for your business — so you're not losing customers to a missed call or a slow reply.",
    tags: ["Books appointments", "Qualifies leads", "Answers 24/7"],
  },
  {
    id: "agents",
    testid: "service-card-agents",
    icon: Bot,
    span: "md:col-span-5",
    title: "AI Agents",
    body: "I build AI agents that take the workload off you — handling the repetitive, time-draining tasks on their own so your team is freed up to focus on the work that actually needs a human.",
    tags: ["Works autonomously", "Handles the busywork", "Frees up your team"],
  },
  {
    id: "workflow",
    testid: "service-card-workflow",
    icon: Workflow,
    span: "md:col-span-7",
    title: "Workflow Automation",
    body: "I connect the tools you already use so information moves on its own — no more copying data between systems by hand.",
    tags: ["Connected tools", "No manual copy-paste"],
  },
  {
    id: "websites",
    testid: "service-card-websites",
    icon: Globe,
    span: "md:col-span-7",
    title: "Websites, Built & Managed",
    body: "I design websites built around how your business actually works — then keep them running, updated, and current, so you never have to think about it.",
    tags: ["Custom-built", "Fully managed", "Always current"],
  },
  {
    id: "social",
    testid: "service-card-social",
    icon: Share2,
    span: "md:col-span-5",
    title: "Social Media Management",
    body: "I manage your social accounts end to end — planning, posting, and staying consistent — so your business shows up online without eating up your week.",
    tags: ["Planned & posted", "Consistent presence", "Done for you"],
  },
];

const Card = ({ s }) => {
  const Icon = s.icon;
  return (
    <motion.div
      variants={fadeUp}
      data-testid={s.testid}
      className={`group relative overflow-hidden rounded-3xl p-7 sm:p-9 glass-light hover:shadow-[0_30px_80px_-30px_rgba(233,51,198,0.4)] transition-shadow duration-500 ${s.span}`}
    >
      <div className="absolute -right-16 -top-16 w-52 h-52 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{ background: "radial-gradient(circle,rgba(233,51,198,0.18),transparent 70%)", filter: "blur(20px)" }} />
      <div className="relative flex items-start justify-between">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-6"
          style={{ background: "linear-gradient(135deg,rgba(233,51,198,0.12),rgba(139,92,246,0.12))", border: "1px solid rgba(233,51,198,0.2)" }}>
          <Icon className="text-fuchsia-600" size={26} strokeWidth={1.8} />
        </div>
        <ArrowUpRight className="text-slate-300 group-hover:text-fuchsia-500 group-hover:-translate-y-1 group-hover:translate-x-1 transition-all" size={22} />
      </div>
      <h3 className="relative font-display font-bold text-slate-900 text-xl sm:text-2xl tracking-tight">{s.title}</h3>
      <p className="relative mt-3 text-slate-700 leading-relaxed max-w-2xl">{s.body}</p>
      <div className="relative mt-6 flex flex-wrap gap-2">
        {s.tags.map((t) => (
          <span key={t} className="font-mono-accent text-[11px] tracking-wide px-3 py-1 rounded-full bg-fuchsia-100 text-fuchsia-900 border border-fuchsia-200">
            {t}
          </span>
        ))}
      </div>
    </motion.div>
  );
};

export const Services = () => {
  return (
    <section id="services" data-testid="services-section" className="relative py-24 sm:py-32">
      <div className="blob w-[30rem] h-[30rem] -left-40 top-40 animate-blob" style={{ background: "rgba(233,51,198,0.14)" }} />
      <div className="blob w-[26rem] h-[26rem] right-0 bottom-0 animate-blob" style={{ background: "rgba(139,92,246,0.12)", animationDelay: "-8s" }} />
      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div initial="hidden" whileInView="show" viewport={viewport} variants={stagger} className="max-w-3xl">
          <motion.div variants={fadeUp} className="font-mono-accent text-xs tracking-[0.25em] uppercase text-fuchsia-600 mb-4">
            What We Do
          </motion.div>
          <motion.h2 variants={fadeUp} className="font-display font-bold text-slate-900 text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1]">
            Strategy, tools, and training —{" "}
            <span className="text-gradient">built around how you actually work.</span>
          </motion.h2>
          <motion.p variants={fadeUp} className="mt-5 text-slate-700 text-lg leading-relaxed">
            Not a template — your process. Full automation systems, not just a single tool.
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={viewport}
          variants={stagger}
          className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-5"
        >
          {services.map((s) => (
            <Card key={s.id} s={s} />
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
